import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './contexts/AuthContext';
import { EntityContextProvider } from './components/EntityContextSwitch';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import ProtectedRoute from './components/ProtectedRoute';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import Layout from './components/Layout';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import EntityManagement from './components/EntityManagement';
import AdminDashboard from './components/Admin/AdminDashboard';
import DataManagement from './components/DataManagement/DataManagement';
import DocumentManagement from './components/DocumentManagement/DocumentManagement';
import UserSettings from './components/Settings/UserSettings';
import { Chat } from './routes/Chat';
import { NotebookRoute } from './routes/Capture/Notebook';
import { Help } from './routes/Help';
import FaanaAgents from './routes/FaanaAgents';
import { Care, PlayerCardPage } from './components/Care';
import BehavioralWorkbench from './components/BehavioralWorkbench';
import theme from './theme';

function App() {
  return (
    <GlobalErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <UserPreferencesProvider>
            <EntityContextProvider>
              <BrowserRouter>
                <ToastContainer
                  position="bottom-right"
                  autoClose={5000}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="colored"
                />
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />

                  {/* Public shareable routes - no auth, no layout */}
                  <Route path="/care/player-card/:entityId" element={<PlayerCardPage />} />

                  {/* Protected routes */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<Layout />}>
                      {/* Legacy routes - keep for backward compatibility */}
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/entity-management" element={<EntityManagement />} />
                      <Route path="/data-management" element={<DataManagement />} />
                      <Route path="/document-management" element={<DocumentManagement />} />
                      <Route path="/admin" element={<AdminDashboard />} />
                      <Route path="/settings" element={<UserSettings />} />

                      {/* New route aliases */}
                      <Route path="/insights" element={<Dashboard />} />
                      <Route path="/chat" element={<Chat />} />
                      <Route path="/entities" element={<EntityManagement />} />

                      {/* Care - CCM (Connected Community Model) */}
                      <Route path="/care" element={<Care />} />

                      {/* Hamburger menu routes */}
                      <Route path="/faana-agents" element={<FaanaAgents />} />
                      <Route path="/behavioral-workbench" element={<BehavioralWorkbench />} />
                      <Route path="/help" element={<Help />} />

                      {/* Capture routes */}
                      <Route path="/capture">
                        <Route index element={<Navigate to="/capture/notebook" replace />} />
                        <Route path="audio" element={<Navigate to="/capture/notebook" replace />} />
                        <Route path="upload" element={<Navigate to="/capture/notebook" replace />} />
                        <Route path="notebook" element={<NotebookRoute />} />
                      </Route>

                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    </Route>
                  </Route>
                </Routes>
              </BrowserRouter>
            </EntityContextProvider>
          </UserPreferencesProvider>
        </AuthProvider>
      </ThemeProvider>
    </GlobalErrorBoundary>
  );
}

export default App;